"use client";

import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import React, { useMemo, useState } from "react";

import DropdownList from "../DropdownList";
import { IErrorProps } from "../error";
import { Input } from "../index";

export interface IOption {
  label: string;
  value: string;
}

export interface IProps extends IErrorProps {
  className?: string;
  style?: React.CSSProperties;
  value: string;
  autoComplete?: HTMLInputElement["autocomplete"];
  name?: HTMLInputElement["name"];
  placeholder?: HTMLInputElement["placeholder"];
  label?: string;
  options: IOption[];
  /**
   * If one of these options get set to the input, don't open the dropdown and
   * accept it as a valid usecase. Used to avoid the dropdown opening when a
   * form is autofilled with valid data that doesn't match the exact options we
   * have in options.
   */
  alternativeOptions?: string[];
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onSelect: (option: IOption) => void;
  onBlur?: () => void;
  disabled?: boolean;
  /**
   * Whether or not to auto select the first option when clicking outside
   * of the input, disabled by default
   */
  autoselect?: boolean;
  "data-test-id"?: string;
  "aria-label"?: string;
}

const SearchDropdownBase: React.ForwardRefRenderFunction<
  HTMLInputElement,
  IProps
> = (
  {
    className,
    style,
    options,
    autoselect,
    value,
    autoComplete = "off",
    name,
    placeholder,
    label,
    error,
    disabled,
    "aria-label": ariaLabel,
    "data-test-id": dataTestId,
    onChange,
    onSelect,
    onBlur,
    ...props
  },
  ref
) => {
  const [isOpen, setIsOpen] = useState(false);

  const search = value?.toLowerCase();
  const alternativeOptions = useMemo(
    () => props.alternativeOptions?.map((option) => option.toLowerCase()),
    [props.alternativeOptions]
  );
  const filteredOptions = options.filter(
    (option: IOption) =>
      !search || option.label.toLowerCase().indexOf(search) !== -1
  );
  const searchInAlternativeOptions =
    isNotUndefined(search) &&
    isNotUndefined(alternativeOptions) &&
    alternativeOptions.includes(search);

  const handleInputOnChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    onChange(event);

    // convert to lower case so matching against alternative options is case insensitive
    const search = event.target.value.toLowerCase();
    if (
      alternativeOptions === undefined ||
      !alternativeOptions.includes(search)
    ) {
      handleDropdownOpen();
    }
  };

  const handleSelectDropDown = (optionValue: string) => {
    const option = filteredOptions.find(
      (option) => option.value === optionValue
    );
    onSelect(option!);
    setIsOpen(false);
  };

  const handleDropdownOpen = () => setIsOpen(true);

  const handleDropdownClose = () => {
    setIsOpen(false);
    if (
      autoselect &&
      search &&
      filteredOptions[0] &&
      !searchInAlternativeOptions
    ) {
      onSelect(filteredOptions[0]);
    }
  };

  const handleBlur = () => {
    handleDropdownClose();
    onBlur?.();
  };

  return (
    <DropdownList<IOption, string>
      className={className}
      style={style}
      options={filteredOptions}
      open={isOpen}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      getLabel={(option) => option.label}
      getValue={(option) => option.value}
      onSelect={handleSelectDropDown}
    >
      <Input
        data-test-id={dataTestId ?? name}
        disabled={disabled}
        label={label}
        error={error}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={handleInputOnChange}
        autoComplete={autoComplete}
        onBlur={handleBlur}
        onFocus={handleDropdownOpen}
        style={{ width: "100%" }}
        aria-label={ariaLabel}
        ref={ref}
      />
    </DropdownList>
  );
};

/**
 * @trackcomponent
 */
export const SearchDropdown: React.ForwardRefExoticComponent<
  IProps & React.RefAttributes<HTMLInputElement>
> = React.forwardRef<HTMLInputElement, IProps>(SearchDropdownBase);
